import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface DisconnectInfo {
  source: 'chat' | 'ai-jobs' | 'idle' | 'unknown';
  reason?: string;
  timestamp: Date;
}

/**
 * Global service to manage socket disconnection popup state.
 * Any socket service can trigger the disconnect popup through this service.
 */
@Injectable({
  providedIn: 'root'
})
export class SocketDisconnectService {
  private showPopupSubject = new BehaviorSubject<boolean>(false);
  private disconnectInfoSubject = new BehaviorSubject<DisconnectInfo | null>(null);

  /** Observable for popup visibility */
  showPopup$: Observable<boolean> = this.showPopupSubject.asObservable();

  /** Observable for disconnect info */
  disconnectInfo$: Observable<DisconnectInfo | null> = this.disconnectInfoSubject.asObservable();

  /**
   * Show the disconnect popup
   * @param source - The socket that triggered the disconnect
   * @param reason - Optional reason for disconnect
   */
  showDisconnectPopup(source: 'chat' | 'ai-jobs' | 'idle' | 'unknown' = 'unknown', reason?: string): void {
    // Only show if not already showing
    if (!this.showPopupSubject.value) {
      console.log(`[SocketDisconnectService] Showing disconnect popup. Source: ${source}, Reason: ${reason}`);
      this.disconnectInfoSubject.next({
        source,
        reason,
        timestamp: new Date()
      });
      this.showPopupSubject.next(true);
    }
  }

  /**
   * Hide the disconnect popup (usually after refresh)
   */
  hideDisconnectPopup(): void {
    this.showPopupSubject.next(false);
    this.disconnectInfoSubject.next(null);
  }

  /**
   * Refresh the page to start a new session
   */
  refreshPage(): void {
    window.location.reload();
  }

  /**
   * Check if popup is currently showing
   */
  isPopupShowing(): boolean {
    return this.showPopupSubject.value;
  }
}
